import { useMemo } from "react";
import {
    type ClientsWorkOverviewSortValue,
    type ClientsWorkOverviewView,
    ClientsWorkOverviewViewSchema,
} from "@common/types/views/clientsWorkOverviewView";
import type { View } from "@common/types/views/views";
import type { BoardDisplayChange } from "@design-system/Board/Board.types";
import {
    type BoardCardFieldColumns,
    BoardDisplayPopover,
} from "@design-system/Board/BoardDisplayPopover";
import { AdjustmentsIcon } from "@icons";
import type { GroupBy } from "../useGroupedTableData";
import { isWorkPersonRole } from "../workPersonGroups";
import type { WorkBoardColumnBy } from "./workBoardColumns";
import {
    WORK_BOARD_LANE_OPTIONS,
    type WorkBoardLaneBy,
    workBoardLaneBySchema,
} from "./workBoardLanes";
import {
    getWorkBoardSort,
    getWorkSortValue,
    WORK_BOARD_SORT_OPTIONS,
} from "./workBoardSort";
import {
    useWorkViewCardFieldIds,
    withWorkViewBoardDisplay,
    withWorkViewCardFields,
} from "./workViewBoardDisplay";
import { getWorkCardFieldConfigs } from "./workCardFields";

type ViewUpdate = (prev: ClientsWorkOverviewView) => ClientsWorkOverviewView;

interface WorkBoardDisplayButtonProps {
    view: ClientsWorkOverviewView;
    onViewChange: (update: ViewUpdate) => void;
    /** The page's grouping; a person grouping draws its role's lanes until a swimlane is picked. */
    groupBy: GroupBy;
    columnBy: WorkBoardColumnBy;
    /** The swimlanes the board draws, shown as the Swimlanes value. */
    laneBy: WorkBoardLaneBy | null;
    isEmptyColumnsShown: boolean;
    /** List's sort, which Board shares. */
    sort: ClientsWorkOverviewSortValue;
    onSortChange: (sort: ClientsWorkOverviewSortValue) => void;
}

const NO_IDS: string[] = [];

/**
 * Board's gear: swimlanes, sort and direction, card fields and empty
 * columns. Sort is List's sort and card fields are List's columns, so a
 * change here is the same change in List; the header picker sets columns.
 * A view with its own card tags (a Kick view, or one saved from it) edits
 * those tags instead, leaving List's columns alone.
 */
export const WorkBoardDisplayButton = ({
    view,
    onViewChange,
    groupBy: pageGroupBy,
    columnBy,
    laneBy,
    isEmptyColumnsShown,
    sort,
    onSortChange,
}: Readonly<WorkBoardDisplayButtonProps>) => {
    const boardSort = getWorkBoardSort(sort);

    const viewCardFields = useWorkViewCardFieldIds({
        options: view.options,
    });
    const cardFields = useMemo((): BoardCardFieldColumns => {
        if (viewCardFields) {
            return {
                columns: viewCardFields.map((id) => ({ id })),
                columnConfigs: getWorkCardFieldConfigs(viewCardFields),
                onColumnsChange: (columns: View["columns"]) => {
                    const cardFieldIds = columns.map(({ id }) => id);
                    onViewChange((prev) => ({
                        ...prev,
                        options: withWorkViewCardFields({
                            options: prev.options,
                            cardFields: cardFieldIds,
                        }),
                    }));
                },
            };
        }
        return {
            columns: view.columns,
            columnConfigs: getWorkCardFieldConfigs(
                view.columns.map(({ id }) => id),
            ),
            onColumnsChange: (columns: View["columns"]) =>
                onViewChange((prev) =>
                    ClientsWorkOverviewViewSchema.parse({ ...prev, columns }),
                ),
        };
    }, [viewCardFields, view.columns, onViewChange]);

    const handleChange = ({
        groupBy,
        isEmptyColumnsShown: shouldShowEmptyColumns,
        sort: nextSort,
    }: BoardDisplayChange) => {
        if (nextSort !== undefined) {
            onSortChange(
                getWorkSortValue({
                    change: { sort: nextSort },
                    listSort: sort,
                }),
            );
        }
        if (groupBy === undefined && shouldShowEmptyColumns === undefined) {
            return;
        }
        const isLeavingPersonGrouping =
            groupBy !== undefined && isWorkPersonRole(pageGroupBy);
        onViewChange((prev) => {
            const options = withWorkViewBoardDisplay({
                options: prev.options,
                change: {
                    ...(groupBy !== undefined && {
                        laneBy: workBoardLaneBySchema.parse(groupBy),
                    }),
                    ...(shouldShowEmptyColumns !== undefined && {
                        isEmptyColumnsShown: shouldShowEmptyColumns,
                    }),
                },
            });
            return {
                ...prev,
                options: isLeavingPersonGrouping
                    ? { ...options, groupBy: "stage" }
                    : options,
            };
        });
    };

    return (
        <BoardDisplayPopover
            value={{
                columnBy,
                groupBy: laneBy,
                sort: boardSort.sort,
                cardFields: NO_IDS,
                isEmptyColumnsShown,
                collapsedColumnIds: NO_IDS,
                collapsedLaneIds: NO_IDS,
            }}
            onChange={handleChange}
            groupByOptions={WORK_BOARD_LANE_OPTIONS}
            groupByLabel="Swimlanes"
            sortOptions={WORK_BOARD_SORT_OPTIONS}
            sortDirection={boardSort.sortDirection}
            onSortDirectionChange={(sortDirection) =>
                onSortChange(
                    getWorkSortValue({
                        change: { sortDirection },
                        listSort: sort,
                    }),
                )
            }
            cardFields={cardFields}
            triggerIcon={<AdjustmentsIcon className="icon-color-gray-900" />}
            triggerButtonProps={{ variant: "primary", size: "md" }}
        />
    );
};
